<i18n locale="en">
{
  "alt": {
    "owner-logo": "Owner Logo"
  }
}
</i18n>

<template>
  <div class="flex-col features-layout">
    <div class="features-layout__mobile-heading flex-col">
      <div class="features-layout__mobile-logo-container">
        <img
          class="features-layout__mobile-logo"
          :alt="t('alt.owner-logo')"
          src="/branding/owner-logo.svg"
        />
      </div>
    </div>

    <div class="features-layout__content flex-row">
      <div
        class="justify-center flex-1 h-full"
        :class="{ 'flex-col': props.centered }"
      >
        <div class="features-layout__lhs-content max-h-full flex-col gap-12">
          <slot />
        </div>
      </div>

      <div class="flex-col features-layout__rhs">
        <div class="features-layout__logo-spacer flex-col">
          <div class="features-layout__logo-container">
            <img
              class="features-layout__logo"
              :alt="t('alt.owner-logo')"
              src="/branding/owner-logo.svg"
            />
          </div>
        </div>

        <div v-if="features" class="flex-col gap-6">
          <div
            v-for="feature of features"
            :key="feature.title"
            class="features-layout__feature-card flex-col gap-3 align-center"
          >
            <Component
              :is="feature.icon"
              class="text-color-secondary"
              size="24"
            />
            <TitleBlock
              :title="feature.title"
              :subtitle="feature.description"
              align="center"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type Component } from 'vue'
import { useI18n } from 'vue-i18n'

import TitleBlock from '@/components/typography/TitleBlock.vue'

export interface Feature {
  icon: Component
  title: string
  description: string
}

const props = defineProps<{
  features?: Feature[]
  centered?: boolean
}>()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';
@use '@/styles/globals';

.features-layout {
  --rhs-width: 500px;
  --mobile-heading-height: 84px;

  height: 100%;
  width: 100%;
  background-color: var(--background-primary);
  overflow-y: auto;

  &__mobile {
    &-heading {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: var(--mobile-heading-height);

      display: flex;
      justify-content: center;
      padding: 0 24px;

      background-color: var(--background-secondary);

      @include globals.control-shadow();

      @include breakpoints.respond-to('lg-and-up') {
        display: none;
      }
    }

    &-logo {
      height: 100%;

      &-container {
        height: 24px;
      }
    }
  }

  &__content {
    height: 100%;
    margin-right: var(--rhs-width);

    @include breakpoints.respond-to('md-and-down') {
      margin-right: 0;
      margin-top: var(--mobile-heading-height);
    }
  }

  &__lhs-content {
    width: 100%;
    max-width: 500px;
    margin: auto;
    padding: 40px 16px;
  }

  &__rhs {
    width: var(--rhs-width);
    flex-shrink: 0;
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    padding: 0 80px;

    background-color: var(--background-secondary);
    border-left: 1px dashed var(--divider);

    @include breakpoints.respond-to('md-and-down') {
      display: none;
    }
  }

  &__logo {
    height: 100%;

    &-container {
      height: 40px;
      margin: auto;
    }

    &-spacer {
      flex: 1;
    }
  }

  &__feature-card {
    border: 1px dashed var(--divider);
    padding: 24px;

    &:last-child {
      margin-bottom: 80px;
    }
  }
}
</style>
